import { FormControl, FormErrorMessage, FormLabel, HStack, InputGroup, InputRightElement, Select, Spinner, Text } from "@chakra-ui/react";
import { FieldError, FieldErrors, FieldValues, UseFormRegister, UseFormTrigger } from "react-hook-form";
import { ChangeEvent, useState } from "react";

import { $keys, $length, $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";
import { G1Country } from "@bcasmart/doxecure-commons/doxecureLocales";
import { CorporationIdentifiersDictionary, CorporationIdentifierType } from "@bcasmart/doxecure-commons/doxecureTypes";

import { AddressForm, AddressFormValues } from "@components/AddressForm/AddressForm";
import { G1Input } from "@components/G1Input";
import { $g1Color } from "@/theme/G1Style";
import { Component } from "@/utils/TypesAndConstants";

type CorporationFormProperties<T extends FieldValues> = {
    label: string;
    required: boolean;
    disabledOrganizationName?: boolean;
    disabledVATNumber?: boolean;
    disabledAddress?: boolean;
    component?: Component;
    countries?: G1Country[];
    corporationIdentifiersDict?: CorporationIdentifiersDictionary;
    selectedCIT?: CorporationIdentifierType;
    prepId?: string;
    showUniqueId?: boolean;
    isSearchInProgress?: boolean;
    data?: {
        organizationName?: string,
        organizationIdentifier?: string,
        city?: string;
    }
    errors: FieldErrors<T>;
    register: UseFormRegister<T>;
    onChange: (e: any) => void;
    onCorporationIdentifierChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onCountryChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
    trigger?: UseFormTrigger<T>;
    validate?: (id: string) => string|boolean;
};

export type CorporationFormValues = {
    organizationName: string,
    organizationId: string,
    vatNumber: string
} & AddressFormValues;

export const CorporationForm = (props: CorporationFormProperties<FieldValues>) => {
    const { label, prepId, required, disabledOrganizationName, disabledVATNumber, disabledAddress, showUniqueId, countries, corporationIdentifiersDict, isSearchInProgress, component, data, errors, register, trigger, onChange, onCorporationIdentifierChange, onCountryChange, validate } = props ;
    const [corporationIdDescription, setCorporationIdDescription] = useState<Nullable<string>>(null) ;
    const [selectedCorporationIdentifierType, setSelectedCorporationIdentifierType] = useState<Nullable<CorporationIdentifierType>>($keys(corporationIdentifiersDict).first()) ;

    const spinnerEl = !!isSearchInProgress ? (
        <InputRightElement>
            <Spinner size="sm" color="gray.500" />
        </InputRightElement>
    ) : null;

    const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
        onCountryChange?.(e);
        setCorporationIdDescription("");
    }

    const handleOrganizationIdTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedCorporationIdentifierType(e.currentTarget.value as CorporationIdentifierType) ;
        setCorporationIdDescription(corporationIdentifiersDict![e.currentTarget.value].desc) ;
        onChange(e);
        trigger?.("organizationIdentifier");
    }

    return (
        <>
            { showUniqueId ? 
                <>
                    <FormControl isRequired={required} isDisabled={disabledVATNumber} marginTop={3}>
                        <FormLabel fontSize="1.25em">Pays de résidence</FormLabel>
                        <Select {...register("organizationCountry")} bg={$g1Color(($ok(component) ? component : 'form')+'.field.bg')} borderColor={$g1Color(($ok(component) ? component : 'form')+'.field.border')} isRequired={required} autoComplete="true" onChange={handleCountryChange}>
                            {countries?.map((country) => (
                                <option value={country.code} key={country.code}>
                                    {country.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl isRequired={required} isDisabled={disabledVATNumber} isInvalid={$ok(errors.organizationIdentifier)} marginTop={3}>
                        <HStack display="flex" >
                            <FormLabel fontSize="1.25em">Identifiant de type</FormLabel>
                            <Select {...register("organizationIdentifierType")} bg={$g1Color(($ok(component) ? component : 'form')+'.field.bg')} borderColor={$g1Color(($ok(component) ? component : 'form')+'field.border')} isRequired={required} autoComplete="true" width="fit-content" onChange={handleOrganizationIdTypeChange}>
                                {$keys(corporationIdentifiersDict)?.map((identifier) => (
                                    <option value={identifier} key={identifier}>
                                        {corporationIdentifiersDict![identifier]?.name}
                                    </option>
                                ))}
                            </Select>
                        </HStack>
                        <G1Input component={component || "form"} registerInput={{...register("organizationIdentifier", { required: required, validate: (value: string) => validate?.(value) })}} placeholder="Identifiant sans espace, ni tiret, ni point" onChange={onCorporationIdentifierChange ?? onChange} />
                        { $ok(corporationIdDescription) && <Text fontSize="smaller" fontStyle="italic" marginTop="0.2em">{corporationIdDescription}</Text> }
                        <FormErrorMessage color="red.200">{(errors.organizationIdentifier as FieldError)?.message ?? `L'identifiant "${selectedCorporationIdentifierType}" est ${$length(data?.organizationIdentifier) === 0 ? 'requis' : 'invalide'}`}</FormErrorMessage>
                    </FormControl>
                </>
                : <FormControl isRequired={required} isDisabled={disabledVATNumber} isInvalid={$ok(errors.vatNumber)} marginTop={3}>
                    <FormLabel fontSize="1.25em">Numéro de TVA intracommunautaire</FormLabel>
                    <G1Input component={component || "form"} registerInput={{ ...register("vatNumber", { required: required, validate: (value: string) => validate?.(value) }) }} textTransform="uppercase" onChange={onChange} />
                    <FormErrorMessage color="red.200">{errors.vatNumber?.message as string}</FormErrorMessage>
                </FormControl>
            }

            <FormControl isRequired={required} isDisabled={disabledOrganizationName} isInvalid={$ok(errors.organizationName)}>
                <FormLabel fontSize="1.25em">{label}</FormLabel>
                <InputGroup>
                    <G1Input component={component || "form"} registerInput={{ ...register("organizationName", { required: required, validate: (value: string) => $length(value.trim()) > 0 }) }} textTransform="uppercase" onChange={onChange} />
                    {spinnerEl}
                </InputGroup>
                <FormErrorMessage color="red.200">Le nom de l'entreprise est {$length(data?.organizationName) === 0 ? 'requis' : 'invalide'}</FormErrorMessage>
            </FormControl>

            <AddressForm prepId={prepId} required={required} component={component} disabled={disabledAddress} displayCountry={!showUniqueId} countries={countries} city={data?.city} isSearchInProgress={isSearchInProgress} errors={errors} register={register} onChange={onChange} />
        </>
    ) ;
} ;